import {isStoppage, useEventsSearch} from '@domain/shifthandover'
import {StoppageDto} from '@hconnect/common/types'
import {useUpmPlantNow, useUpmPlantTimezone} from '@hconnect/upm'
import moment from 'moment-timezone'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {getStoppageValidatorConfig} from '../components'
import {COMMON_SHIFT_EVENT_WIDGET_NAMESPACE} from '../consts'

export const useTypeSpecificValidatorConfig = (
  plantId: string,
  {stoppageStart, stoppageEnd, id, originalSplitStoppageId, upmDepartmentId}: Partial<StoppageDto>,
  skipOverlapValidation?: boolean
) => {
  const {t} = useTranslation(COMMON_SHIFT_EVENT_WIDGET_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const memoizedEndDate = useMemo(() => stoppageEnd || moment.utc(), [stoppageEnd])

  const currentStoppageId = id || originalSplitStoppageId

  const {data} = useEventsSearch(
    {
      plantId,
      shifts: [],
      getPlantNow,
      timezone,
      pageNumber: 0,
      // fetch one more event than needed to check if there is an existing event other than the one being edited (in case of edit mode)
      itemsPerPage: currentStoppageId ? 2 : 1,
      input: {
        timeRange: {
          startDate: moment.utc(stoppageStart).add(1, 'millisecond'),
          endDate: moment.utc(memoizedEndDate).subtract(1, 'millisecond')
        },
        upmDepartmentIds: upmDepartmentId ? [upmDepartmentId] : undefined
      }
    },
    {enabled: !!upmDepartmentId && !!stoppageStart}
  )

  return useMemo(() => {
    const existingEvent = (
      currentStoppageId
        ? data?.events?.filter((event) => event.id !== currentStoppageId)
        : data?.events
    )?.[0]
    return new Map(
      getStoppageValidatorConfig(
        t,
        existingEvent && isStoppage(existingEvent) ? existingEvent : undefined,
        skipOverlapValidation
      )
    )
  }, [data?.events, t, currentStoppageId, skipOverlapValidation])
}
